import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { rewriteLegacyAppUrl } from 'app/core/auth/legacy-route-url.util';
import { UserService } from 'app/core/user/user.service';
import { map, take } from 'rxjs';

/**
 * Map of top-level route segments to permission key from getPerms() response.
 * Must stay in sync with permissionKey in navigation data.ts.
 */
const ROUTE_PERMISSION_MAP: Record<string, string> = {
    'tracker': 'trackers',
    'procedures': 'procedures',
    'hyper-forms': 'hyper_forms',
    'teams-chat': 'group_chat',
    'tasks': 'tasks',
    'activities': 'activities',
    'reports': 'generated_report',
    'ai-agents': 'agents',
    'my-agents': 'agents',
    'custom-agents': 'custom_agents',
    'admin-custom-agents': 'custom_agents',
    'data-sources': 'data_source',
    // The backend gates `/v1/automation/` on `PermissionsEnum.automation`, so without this entry a
    // deep link rendered the page and every call on it answered 403. The payload has carried
    // `automation` since `users/get-perms` was written; it is merged from the account root exactly
    // as every other module is.
    'automations': 'automation',
    // The CRM module. The backend gates BOTH `/v1/crm/` and `/v1/crm-sync/` on the same
    // `PermissionsEnum.crm` feature, so one key covers the whole module; the ERP Sync tab inside it
    // is additionally role-gated, not permission-gated.
    //
    // Without this entry the route is not permission-checked AT ALL — `resolvePermissionKey`
    // returns null for an unmapped first segment and the guard opens with `if (!permissionKey)
    // return true`. That is the same failure `automations` above was added to fix.
    'crm': 'crm',
    // Legacy top-level Data Quality URL. The page is now a sub-unit of Data Sources at
    // `/data-sources/data-quality` and `app.routes.ts` redirects this path there; the entry stays
    // so an old link is refused here rather than one hop later. The live path is keyed by the
    // `data-sources` branch of `resolvePermissionKey`, NOT by this entry.
    'data-quality': 'cognition',
    // Messaging channels (workspace ADR 0016). The backend gates BOTH `/v1/channels/` and
    // `/v1/contacts/` on the same `PermissionsEnum.channels` key, and the contact directory is a
    // tab inside this feature, so one entry covers the whole module. The route is additionally
    // `RoleGuard`-ed to `company`: managing a connection means holding provider credentials, which
    // stays the organization admin's alone. Both apply - a member is stopped by the role, and an
    // organization without the feature is stopped by this.
    'channels': 'channels',
    // The approvals inbox. It shipped ungated because `/v1/approvals` carried no
    // `PERMISSION_ROUTE_RULES` entry; the backend now mints `PermissionsEnum.approvals` and gates the
    // prefix on it, granting the key to every existing organization at rollout, so the module is a
    // first-class entitlement like every other line here.
    //
    // **Deleting this line does not break loudly, it opens the page for everybody.**
    // `resolvePermissionKey` returns `null` for an unmapped first segment and the guard answers
    // `if (!permissionKey) return true`, so the inbox would render for a tenant without the module
    // and every call on it would answer 403 - the same silent failure the `automations` and `crm`
    // entries were added to fix. `permission.guard.spec.ts` asserts the page is CLOSED when the key
    // is absent from the payload, which is the assertion that fails if this entry goes away.
    //
    // Three things name this key and all three must agree: this entry, `permissionKey: 'approvals'`
    // on the nav item in `mock-api/common/navigation/data.ts`, and `'approvals'` in
    // `MODULE_PERMISSION_KEYS` (`core/user/user.service.ts`) - the sidebar compares the payload value
    // with `=== true`, so a key the normaliser leaves as `'full_access'` hides the item from the very
    // people who hold it.
    'approvals': 'approvals',
    // The dashboards module (workspace ADR 0021): composed HTML documents whose data resolves at
    // view time. The backend mints `PermissionsEnum.dashboards` and gates the `/v1/dashboards/`
    // prefix on it in `PERMISSION_ROUTE_RULES`, so a tenant without the module answers 403 on
    // every call the page makes.
    //
    // The key is `disabled` for every existing tenant and is deliberately NOT backfilled, unlike
    // `approvals`: nothing is being gated retroactively, so a grant would hand every organization
    // a module it has not bought. That makes the closed case the normal one here, not the
    // exception, and it is the reason this entry had to land BEFORE any dashboards feature code.
    //
    // Until 2026-08-24 the guard carried an early `if (firstSegment === 'dashboards') return true`
    // that short-circuited the whole check, and the map carried no entry, so the route was open to
    // everybody TWICE OVER. Both halves were removed together: deleting only the early return would
    // have left `resolvePermissionKey` answering `null`, which the guard also treats as a grant.
    //
    // As with every line above, three places name this key and all three must agree: this entry,
    // `'dashboards'` in `MODULE_PERMISSION_KEYS` (`core/user/user.service.ts`), and the
    // `permissionKey` on the `dashboards` nav item in `mock-api/common/navigation/data.ts`. All
    // three exist as of 2026-08-24, when the module gained its frontend routes (`/dashboards` for
    // the list, `/dashboards/view/:dashboardId` for the viewer).
    'dashboards': 'dashboards',
    // Harvester price audits (workspace ADR 0026): the backend gates `/v1/harvester/` on the same key.
    'harvester': 'harvester',
    // Legacy keys (redirect guard sends users to unified routes)
    'ai-forms': 'hyper_forms',
    'chat': 'sai_chat',
    'chat-ai': 'sai_chat',
};

const OVERVIEW_URL = '/overview';

const LEGACY_ROUTE_SEGMENTS = new Set([
    'my-agents',
    'autonomous-reports',
    'ai-forms',
    'chat',
    'chat-ai',
]);

function resolvePermissionKey(url: string): string | null {
    const segments = url.split('?')[0].split('/').filter(Boolean);
    const firstSegment = segments[0];

    if (firstSegment === 'reports') {
        if (segments[1] === 'report') {
            return null;
        }
        if (segments[1] === 'autonomous-reports') {
            return null;
        }
        return 'generated_report';
    }

    if (firstSegment === 'ai-chat') {
        if (segments[1] === 'sai-master') {
            return 'sai_chat';
        }
        return null;
    }

    // Data Quality is a SUB-UNIT of Data Sources, not a module of its own (workspace ADR 0017), so
    // one prefix covers two entitlements and the narrower one is the nested path.
    //
    // **This branch is the only thing that keeps them apart.** Every lookup below is by FIRST
    // segment, so without it `/data-sources/data-quality` resolves to `data_source` - a key most
    // tenants hold - and the page opens for an organization with no calibration entitlement, whose
    // every backend call then answers 403. Nothing throws and nothing logs; the symptom is a
    // working-looking page full of errors, which is exactly the defect the `automations` entry was
    // added to fix.
    //
    // It is the frontend half of a hazard the backend carries in the same shape: there,
    // `inferPermissionFromPath` takes the FIRST MATCHING PREFIX, so `/data_source/data-quality/`
    // must sit above `/data_source/` in `PERMISSION_ROUTE_RULES`. Same trap, same cause (a sub-unit
    // that does not inherit its parent's access posture), two different controls because the two
    // resolvers match differently. Changing one without the other splits the gate.
    if (firstSegment === 'data-sources') {
        return segments[1] === 'data-quality' ? 'cognition' : 'data_source';
    }

    // `/dashboards` carries TWO populations and only one of them is the module.
    //
    // Every `/dashboards/*` URL in the wild today is a pre-migration bookmark that
    // `legacyDashboardsRedirectGuard` rewrites to a flat route (`/dashboards/activities` ->
    // `/activities`, bare `/dashboards` -> `/overview`). That guard is a `canActivate` on the
    // route; THIS one is a `canActivateChild` on the parent, and Angular runs the parent's child
    // guards FIRST. So gating the whole prefix on the module key would bounce every legacy
    // bookmark to Overview before the rewrite could ever run, and the key is `disabled` for every
    // existing tenant, which makes that the case for ALL of them rather than an edge.
    //
    // A URL the rewriter changes is therefore not a dashboards-module URL. It resolves to no key
    // here, the redirect guard forwards it, and the DESTINATION route is gated on its own key by
    // this same guard one navigation later. Nothing is opened by the deferral: `/activities` still
    // answers to `activities`.
    //
    // A BARE `/dashboards` changed populations on 2026-08-24 and now takes the module branch. It
    // was a pre-migration shell root the rewriter sent to Overview, so it resolved to no key; it is
    // the module's list page now, `rewriteLegacyDashboardsPath` leaves it alone, and it gates like
    // any other module URL. A tenant without the key still ends on Overview, which is exactly where
    // the old rewrite sent them, so no bookmark changed its destination.
    //
    // Anything the rewriter leaves alone is a real module path and gates on `dashboards` through
    // the map entry below. Same shape as the `data-sources` branch above: one prefix, two
    // entitlement answers, decided by what follows the first segment.
    if (firstSegment === 'dashboards' && rewriteLegacyAppUrl(url) !== url) {
        return null;
    }

    return ROUTE_PERMISSION_MAP[firstSegment] ?? null;
}

export const PermissionGuard: CanActivateChildFn = (_childRoute, state) => {
    const router = inject(Router);
    const userService = inject(UserService);

    const segments = state.url.split('?')[0].split('/').filter(Boolean);
    const firstSegment = segments[0];

    // Warm the permissions cache on the way past, even for a route that needs none.
    //
    // Navigation visibility now reads a synchronous snapshot of this payload
    // (`module-permission-snapshot.ts`), and until something loads it once, that snapshot is empty
    // and a permission-gated nav item stays hidden from somebody who genuinely has it. Every early
    // return below is a route that needs no permission of its own - and `/overview` is the DEFAULT
    // landing page, so without this the common case is exactly the one that never loads.
    //
    // It is one request, cached for the session by `getPermissionsForGuard`, and it is fire and
    // forget: the guard's own answer never depends on it, so a failure here cannot block navigation.
    userService.getPermissionsForGuard().pipe(take(1)).subscribe({ error: () => undefined });

    if (firstSegment === 'overview' || firstSegment === 'dashboard') {
        return true;
    }

    if (LEGACY_ROUTE_SEGMENTS.has(firstSegment)) {
        return true;
    }

    if (firstSegment === 'pages') {
        return true;
    }

    const permissionKey = resolvePermissionKey(state.url);
    if (!permissionKey) {
        return true;
    }

    return userService.getPermissionsForGuard().pipe(
        take(1),
        map((perms) => {
            const rawPermission = perms?.[permissionKey];
            const normalizedPermission = String(rawPermission ?? '')
                .trim()
                .toLowerCase();
            const hasAccess =
                rawPermission === true ||
                normalizedPermission === 'full_access' ||
                normalizedPermission === 'read_only';
            if (!hasAccess) {
                router.navigateByUrl(OVERVIEW_URL);
                return false;
            }
            return true;
        })
    );
};
